/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import {
	View,
	Text,
	CircleSpinner,
	Pressable,
	Button,
	Icon,
	Radiobutton,
	ErrorText,
} from '@components/atoms'

import {
	useAppSelector,
	useAppDispatch,
	paymentActions,
	ApiGetDataStatus,
	PaymentShippingType,
	PaymentStateType,
} from '@redux/index'
import { ShippingCostType } from '@schemas'
import { produce } from 'immer'
import { grey, pink } from '@mui/material/colors'

export const PaymentShipping = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const shippingCostStatus: ApiGetDataStatus = useAppSelector(
		state => state.payment.shippingCostStatus,
	)
	const shippingCostList: ShippingCostType[] = useAppSelector(
		state => state.payment.shippingCostList,
	)
	const errorMessages: PaymentStateType['paymentErrorMessages'] = useAppSelector(
		state => state.payment.paymentErrorMessages,
	)
	const shipping: PaymentShippingType = useAppSelector(
		state => state.payment.paymentShipping,
	)

	// state
	const [selectedShippingCost, setSelectedShippingCost] =
		React.useState<ShippingCostType>({
			companyName: '',
			rate: 0,
			estimatedDeliveryDate: '',
			currency: '',
		})

	const onShippingCostClick = (shippingProps: ShippingCostType) => {
		setSelectedShippingCost(shippingProps)

		const newErrorMessage = produce(errorMessages, draft => {
			draft.shipping.companyName = ''
		})

		reduxDispatch(paymentActions.PAYMENT_ERROR_MESSAGES(newErrorMessage))

		const newShipping = produce(shipping, draft => {
			draft.companyName = shippingProps.companyName
			draft.usd = shippingProps.rate
			draft.deliveryDays = isNaN(Number(shippingProps.estimatedDeliveryDate))
				? 0
				: Number(shippingProps.estimatedDeliveryDate)
		})

		reduxDispatch(paymentActions.PAYMENT_SHIPPING(newShipping))
	}

	const onShippingCostReloadClick = () => {
		reduxDispatch(paymentActions.TRIGGER_GET_SHIPPING_COST(true))
	}

	return (
		<View css={style} className="infoView">
			<View className="infoTitleView">
				<Text category="h3">Shipping Cost</Text>
			</View>

			<View className="infoInnerView shipping">
				<View
					className={`shippingListView ${
						errorMessages.shipping.companyName !== '' ? 'error' : ''
					}`}
				>
					{shippingCostStatus === 'init' || shippingCostStatus === 'loading' ? (
						<View className="shippingCostSpinnerView">
							<CircleSpinner />
						</View>
					) : shippingCostStatus === 'empty' ? (
						<View>
							<Text>There is no available delivery service for the entered address.</Text>
							<Button variant="contained" onClick={onShippingCostReloadClick}>
								<Icon name="reload" css={{ color: 'white', marginRight: '0.5rem' }} />
								Search and Reload
							</Button>
						</View>
					) : shippingCostStatus === 'ok' && shippingCostList.length > 0 ? (
						<View>
							{shippingCostList.map((anItem, index) => {
								const productName =
									anItem.companyName === 'fedex'
										? 'FeDex'
										: anItem.companyName === 'ups'
										? 'UPS'
										: anItem.companyName
								return (
									<View
										key={`${anItem.companyName}_${index}`}
										className="shippingCostItemView"
									>
										<Pressable
											onClick={() => onShippingCostClick(anItem)}
											className="shippingCostItemButton"
										>
											<Radiobutton
												value={anItem.companyName}
												onRadioChange={() => onShippingCostClick(anItem)}
												selectedValue={selectedShippingCost.companyName}
											/>
											<Text>{productName}</Text>
											<Text className="shippingCostUsd">
												{anItem.rate.toLocaleString('en-US', {
													style: 'currency',
													currency: 'USD',
												})}
											</Text>
										</Pressable>
									</View>
								)
							})}
						</View>
					) : null}
				</View>
			</View>

			<ErrorText>{errorMessages.shipping.companyName}</ErrorText>
		</View>
	)
}

const style = css({
	'.infoInnerView': {
		paddingLeft: '1rem',
		paddingRight: '1rem',

		'.shippingListView': {
			padding: '0.5rem',
			backgroundColor: grey[100],
			border: '1px solid',
			borderColor: grey[300],
			borderRadius: '5px',

			'.shippingCostSpinnerView': {
				height: '5rem',
			},

			'.shippingCostItemView': {
				width: '100%',
				backgroundColor: 'white',
				borderRadius: '5px',

				'.shippingCostItemButton': {
					position: 'relative',
					display: 'flex',
					justifyContent: 'flex-start',
					width: '100%',

					'.shippingCostUsd': {
						position: 'absolute',
						width: 'fit-content',
						right: '1rem',
					},
				},
			},

			'&.error': {
				borderColor: pink['A400'],
				backgroundColor: pink[100],
			},
		},
	},
})
